import { useCallback, useRef, useState } from "react";
import { api, unwrapEnvelope } from "@/lib/api";
import { hasPreviewToken } from "./normalizeSource";
import {
  clearPreviewCache,
  getCachedPreview,
  setCachedPreview,
} from "./previewCache";

/**
 * Lazy buy-source PDP preview — hover with preview.token only.
 *
 * card.preview.token
 *   → open overlay + skeleton
 *   → POST /buy-sources/preview { preview_token }
 *   → GET image_endpoint + X-Buy-Source-Preview-Token
 *   → blob object URL (session cached)
 */
export function useSourcePreview() {
  const [state, setState] = useState({
    open: false,
    loading: false,
    error: null,
    data: null,
    source: null,
  });
  const inflight = useRef(null);
  const imageObjectUrl = useRef(null);

  const revokeActiveUrl = useCallback(() => {
    if (imageObjectUrl.current) {
      URL.revokeObjectURL(imageObjectUrl.current);
      imageObjectUrl.current = null;
    }
  }, []);

  const close = useCallback(() => {
    inflight.current = null;
    imageObjectUrl.current = null;
    setState({ open: false, loading: false, error: null, data: null, source: null });
  }, []);

  const openPreview = useCallback(
    async (source) => {
      if (!hasPreviewToken(source)) {
        return;
      }

      const previewToken = source.preview_token || source.preview?.token;
      const cached = getCachedPreview(previewToken);
      if (cached?.imageUrl) {
        imageObjectUrl.current = cached.imageUrl;
        setState({
          open: true,
          loading: false,
          error: null,
          data: cached,
          source,
        });
        return;
      }

      setState({ open: true, loading: true, error: null, data: null, source });
      if (imageObjectUrl.current) {
        revokeActiveUrl();
      }

      const req = (async () => {
        const createRes = await api.buySourcesPreviewRequest(previewToken);
        const created = unwrapEnvelope(createRes);
        const previewId = created?.preview_id;
        const imageEndpoint = created?.image_endpoint;
        if (!imageEndpoint) {
          throw new Error("Preview image endpoint missing");
        }
        const imageUrl = await api.buySourcesPreviewImage(imageEndpoint, previewToken);
        return { preview_id: previewId, imageUrl, image_endpoint: imageEndpoint };
      })();

      inflight.current = req;
      try {
        const data = await req;
        if (inflight.current !== req) {
          if (data?.imageUrl) URL.revokeObjectURL(data.imageUrl);
          return;
        }
        setCachedPreview(previewToken, data);
        imageObjectUrl.current = data.imageUrl;
        setState({ open: true, loading: false, error: null, data, source });
      } catch (err) {
        if (inflight.current !== req) return;
        setState({
          open: true,
          loading: false,
          error: err?.message || "Preview unavailable",
          data: null,
          source,
        });
      }
    },
    [revokeActiveUrl]
  );

  return { preview: state, openPreview, close, clearPreviewCache };
}
